import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import {
  type GetInterviewsResponse,
  InterviewStatus,
  type InterviewWithDetails,
  transformResponseIntoInterview,
  useFetchPaginatedInterviews,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { subDays } from "date-fns";

import { PlacementApplicationStatus } from "../types/fetchPlacements.schema";

export function transformPaginatedInterviews(
  pages: GetInterviewsResponse[] | undefined,
  timezone: string
): InterviewWithDetails[] | undefined {
  return pages?.flatMap((page) =>
    page.data.map((interview) =>
      transformResponseIntoInterview(interview, page.included ?? [], timezone)
    )
  );
}

export function useInterviewAttendanceOutcome(
  {
    workerId,
    withDismissed,
  }: {
    workerId: string;
    withDismissed: boolean;
  },
  options: UseInfiniteQueryOptions<GetInterviewsResponse, AxiosError> = {}
) {
  const isOfferOutcomeEnabled = useCbhFlag(CbhFeatureFlag.OFFER_OUTCOME, {
    defaultValue: false,
  });
  const today = new Date();
  return useFetchPaginatedInterviews(
    {
      sort: "-start",
      filter: {
        workerId,
        status: InterviewStatus.ATTENDANCE_CONFIRMED,
        start: {
          gt: subDays(
            new Date(today.getFullYear(), today.getMonth(), today.getDate()),
            3
          ).toISOString(),
        },
        ...(withDismissed ? { "workerOutcome.dismissedAt": { isNull: true } } : undefined),
        "workerOutcome.result": { isNull: true },
        "placementApplication.status": [
          PlacementApplicationStatus.ACCEPTED,
          PlacementApplicationStatus.APPLIED,
          PlacementApplicationStatus.INTERVIEW,
          PlacementApplicationStatus.INTERVIEW_CONFIRMED,
          PlacementApplicationStatus.INTERVIEW_CANCELLED,
          PlacementApplicationStatus.INTERVIEW_HELD,
          PlacementApplicationStatus.INTERVIEW_NOT_CONFIRMED,
          PlacementApplicationStatus.INTERVIEW_NOT_SHOWED,
          PlacementApplicationStatus.INTERVIEW_TURNED_AWAY,
        ].join(","),
      },
    },
    { ...options, enabled: isOfferOutcomeEnabled && options?.enabled }
  );
}
